import React, { ComponentProps, KeyboardEventHandler, MouseEvent, useEffect, useMemo, useRef, useState } from 'react'

import { Button, Icon, IconButton } from '@/shared/components'
import { scrollbarStyle } from '@/shared/styles'
import { pxToRem } from '@/shared/styles/themes'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { ClickAwayListener, Fade, MenuList, Paper, Popper, styled, Typography } from '@mui/material'

import { DropdownContext, DropdownContextType } from './DropdownContext'

export type DropdownProps = {
  children: React.ReactNode
  value: string
  fullHeight?: boolean
  onSelectItem?: (value: string) => void
  onOpenChange?: (open: boolean) => void
  error?: boolean
  border?: boolean
  icon?: React.ReactNode
  isIconButton?: boolean
  allowPropagation?: boolean
  slotProps?: {
    iconButton?: ComponentProps<typeof IconButton>
    button?: ComponentProps<typeof Button>
    popper?: Partial<ComponentProps<typeof Popper>>
    menuList?: ComponentProps<typeof MenuList>
  }
}

export const Dropdown: React.FC<DropdownProps> = ({ children, value, error, allowPropagation, fullHeight, isIconButton, slotProps, icon, onSelectItem = () => {}, onOpenChange }) => {
  const menuListRef = useRef<HTMLUListElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!allowPropagation) {
      event.preventDefault()
      event.stopPropagation()
    }

    setAnchorEl(event.currentTarget)
    setOpen(true)
  }

  const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      event.stopPropagation()

      setAnchorEl(event.currentTarget)
      setOpen(true)

      if (open && menuListRef) {
        menuListRef?.current?.focus()
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClose = (event: any) => {
    event.preventDefault()
    event.stopPropagation()

    setTimeout(() => {
      setOpen(false)
    }, 100)
  }

  const defaultContext: DropdownContextType = useMemo(() => ({
    open,
    onSelectItem,
    setDropdownOpen: setOpen,
  }), [onSelectItem, open])

  // Callback for when the dropdown is opened or closed
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(open)
    }
  }, [open, onOpenChange])

  return (
    <DropdownContext.Provider value={defaultContext}>
      {isIconButton ? (
        <StyledIconButton tooltipTitle={value} onClick={handleClick} onKeyDown={onKeyDown} $open={open} $error={error} {...slotProps?.iconButton}>
          {icon}
        </StyledIconButton>
      ) : (
        <StyledButton text tooltipTitle={value} onClick={handleClick} onKeyDown={onKeyDown} $open={open} $error={error} {...slotProps?.button}>
          {icon}
          <StyledTitleContainer>
            <StyledDropdownValue variant="body1">
              {value}
            </StyledDropdownValue>
            <StyledIcon icon={faChevronDown} />
          </StyledTitleContainer>
        </StyledButton>
      )}
      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        {...slotProps?.popper}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper sx={{ marginTop: pxToRem(4), marginBottom: pxToRem(4) }}>
              <ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                <StyledMenuList ref={menuListRef} $fullHeight={fullHeight} {...slotProps?.menuList}>
                  {children}
                </StyledMenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </StyledPopper>
    </DropdownContext.Provider>
  )
}

export const StyledIconButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== '$open' && prop !== '$error' }) <{ $open?: boolean, $error?: boolean }>`
  ${(props) => props.$open && `
    background-color: ${props.theme.palette.action.hover};
  `}
  
  ${(props) => props.$error && `
    border: 1px solid ${props.theme.palette.error.main};
  `}
`

export const StyledButton = styled(Button, { shouldForwardProp: (prop) => prop !== '$open' && prop !== '$error' }) <{ $open: boolean, $error?: boolean }>`
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

  ${(props) => props.$open && `
  background-color: ${props.theme.palette.action.hover};
  `}

  ${(props) => props.$error && `
    border: 1px solid ${props.theme.palette.error.main};
  `}
`

export const StyledPopper = styled(Popper)`
  z-index: 110;
  background-color: ${(props) => props.theme.palette.background.paper};
  border-radius: ${(props) => props.theme.typography.pxToRem(4)};
`

export const StyledDropdownContainer = styled('div')`
  display: inline-block;
`

export const StyledIcon = styled(Icon)`
  color: ${props => props.theme.palette.text.primary};
  margin-left: ${props => props.theme.typography.pxToRem(8)};
  font-size: ${props => props.theme.typography.pxToRem(14)};
`

export const StyledTitleContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`

export const StyledDropdownValue = styled(Typography)`
  color: ${props => props.theme.palette.text.primary};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const StyledMenuList = styled(MenuList, {
  shouldForwardProp: (prop) => prop !== '$fullHeight',
}) <{ $fullHeight?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: ${props => props.theme.typography.pxToRem(4)};
  gap: 4px;
  overflow-y: auto;
  ${props => !props.$fullHeight && `
    max-height: ${props.theme.typography.pxToRem(240)};
  `}
  ${scrollbarStyle};

  li:first-of-type {
    &:after {
      border-radius: ${props => props.theme.typography.pxToRem(8)} ${props => props.theme.typography.pxToRem(8)} 0 0;
    }
  }

  li:last-of-type {
    &:after {
      border-radius: 0 0 ${props => props.theme.typography.pxToRem(8)} ${props => props.theme.typography.pxToRem(8)};
    }
  }
`
